import type { ScaleTime } from "d3-scale";
import { forwardRef, useId } from "react";
import { type LiveTimePreviewHandle, useLiveTimeTicks } from "./useLiveTimeTicks";

/**
 * Subtle vertical gridlines rising from the time-axis tick positions into the
 * plot, so operators can anchor a data point to its time. Shares the exact
 * calendar tick positions of LiveTimeAxisBottom (same useLiveTimeTicks hook) and
 * re-ticks live against the gesture, so the lines stay under the labels and in
 * lockstep with the data. Rendered behind the series; overscan ticks are clipped
 * to the plot x-range so lines slide off the edge instead of bleeding into the
 * y-axis gutter (matching how the axis labels clip).
 */
interface LiveTimeGridlinesProps {
  scale: ScaleTime<number, number>;
  width: number;
  /** Plot-area top and bottom in px (y the lines span). */
  top: number;
  bottom: number;
}

export const LiveTimeGridlines = forwardRef<LiveTimePreviewHandle, LiveTimeGridlinesProps>(
  ({ scale, width, top, bottom }, ref) => {
    const { activeScale, ticks, rangeStart, rangeEnd } = useLiveTimeTicks(scale, width, ref);
    const clipId = useId();
    const clipLeft = Math.min(rangeStart, rangeEnd);
    const clipWidth = Math.abs(rangeEnd - rangeStart);
    return (
      <g aria-hidden="true">
        <defs>
          <clipPath id={clipId}>
            <rect x={clipLeft} y={top} width={clipWidth} height={bottom - top} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          {ticks.map((d) => {
            const x = activeScale(d);
            return (
              <line
                key={d.getTime()}
                x1={x}
                x2={x}
                y1={top}
                y2={bottom}
                stroke="var(--color-border-muted)"
                strokeWidth={1}
              />
            );
          })}
        </g>
      </g>
    );
  }
);

LiveTimeGridlines.displayName = "LiveTimeGridlines";
